import { Fragment, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  startOfMonth,
} from 'date-fns';
import {
  Calendar as CalendarIcon,
  ChevronRight,
  MapPin,
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { formatDateRange } from '@/utils/formatters';

export interface CalendarRodeoUserEvent {
  eventId: string;
  /** Pre-rendered display label, e.g. "#10 Open — Heeler". */
  label: string;
}

export interface CalendarRodeo {
  id: string;
  name: string;
  start_date?: string | null;
  end_date?: string | null;
  location?: string | null;
  organization_name?: string | null;
  /** Events in this rodeo the current user is personally registered for. */
  userRegisteredEvents?: CalendarRodeoUserEvent[];
}

interface RodeoCalendarViewProps {
  rodeos: CalendarRodeo[];
  /**
   * IDs of rodeos the current user has personal registrations in. Days that
   * intersect any of these rodeos get a red dot in the calendar cell.
   */
  registeredRodeoIds?: Set<string>;
  /** Calendar header title. Defaults to "Rodeo Calendar". */
  title?: string;
  /**
   * Singular noun used in legend/empty-state copy ("3 jackpots in view"…).
   * Defaults to "rodeo".
   */
  itemNoun?: string;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Parse YYYY-MM-DD as local-time midnight so date-only strings don't drift
// a day in UTC-negative zones (May 1 → Apr 30 in PT/MT/CT/ET otherwise).
function parseLocalDate(input?: string | null): Date | null {
  if (!input) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(input);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

export function RodeoCalendarView({
  rodeos,
  registeredRodeoIds,
  title = 'Rodeo Calendar',
  itemNoun = 'rodeo',
}: RodeoCalendarViewProps) {
  const navigate = useNavigate();

  const today = useMemo(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), t.getDate());
  }, []);

  const months = useMemo(
    () => [
      startOfMonth(today),
      startOfMonth(addMonths(today, 1)),
      startOfMonth(addMonths(today, 2)),
    ],
    [today],
  );

  // Map dateKey → rodeos taking place that day. Multi-day rodeos contribute
  // an entry for every day in their range so the calendar can highlight the
  // entire run, not just the start date.
  const rodeosByDay = useMemo(() => {
    const map = new Map<string, CalendarRodeo[]>();
    const windowStart = months[0];
    const windowEnd = endOfMonth(months[months.length - 1]);
    for (const r of rodeos) {
      const start = parseLocalDate(r.start_date);
      if (!start) continue;
      const end = parseLocalDate(r.end_date) ?? start;
      if (end < windowStart || start > windowEnd) continue;
      const span = eachDayOfInterval({
        start: start < windowStart ? windowStart : start,
        end: end > windowEnd ? windowEnd : end,
      });
      for (const d of span) {
        const key = dayKey(d);
        const list = map.get(key);
        if (list) {
          if (!list.some((existing) => existing.id === r.id)) list.push(r);
        } else {
          map.set(key, [r]);
        }
      }
    }
    return map;
  }, [rodeos, months]);

  const totalInWindow = useMemo(() => {
    const seen = new Set<string>();
    for (const list of rodeosByDay.values()) {
      for (const r of list) seen.add(r.id);
    }
    return seen.size;
  }, [rodeosByDay]);

  const onSelectRodeo = (rodeoId: string) => {
    sessionStorage.setItem('rodeos-scroll', String(window.scrollY));
    navigate(`/rodeos/${rodeoId}`);
  };

  return (
    <div className="overflow-hidden rounded-xl border bg-gradient-to-br from-card to-card/60 shadow-sm">
      <div className="flex items-center gap-2 border-b bg-muted/30 px-4 py-3 sm:px-6">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
          <CalendarIcon className="h-4 w-4" aria-hidden="true" />
        </span>
        <h2 className="text-base font-semibold tracking-tight">{title}</h2>
        <span className="ml-auto text-xs font-medium text-muted-foreground">
          {totalInWindow === 0
            ? `No ${itemNoun}s scheduled in the next 3 months`
            : `${totalInWindow} ${itemNoun}${totalInWindow === 1 ? '' : 's'} in view`}
        </span>
      </div>
      <div className="px-4 py-5 sm:px-6 sm:py-6">
        <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start sm:justify-center">
          {months.map((m, i) => (
            <Fragment key={m.getTime()}>
              {i > 0 && (
                <div
                  aria-hidden="true"
                  className="hidden h-64 w-px self-center bg-gradient-to-b from-transparent via-border to-transparent sm:block sm:mx-4 md:mx-8"
                />
              )}
              <MonthGrid
                month={m}
                today={today}
                rodeosByDay={rodeosByDay}
                registeredRodeoIds={registeredRodeoIds}
                itemNoun={itemNoun}
                onSelectRodeo={onSelectRodeo}
              />
            </Fragment>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t bg-muted/20 px-4 py-2.5 text-xs text-muted-foreground sm:px-6">
        <span className="flex items-center gap-1.5">
          <span className="block h-2.5 w-2.5 rounded-sm bg-primary/25 ring-1 ring-primary/40" />
          {itemNoun.charAt(0).toUpperCase() + itemNoun.slice(1)} day
        </span>
        <span className="flex items-center gap-1.5">
          <span className="block h-1.5 w-1.5 rounded-full bg-primary" />
          You&apos;re registered
        </span>
        <span className="flex items-center gap-1.5">
          <span className="block h-2.5 w-2.5 rounded-sm ring-2 ring-primary" />
          Today
        </span>
        <span className="ml-auto hidden sm:block">
          Tap a highlighted day to see {itemNoun}s and jump to details.
        </span>
      </div>
    </div>
  );
}

interface MonthGridProps {
  month: Date;
  today: Date;
  rodeosByDay: Map<string, CalendarRodeo[]>;
  registeredRodeoIds?: Set<string>;
  itemNoun: string;
  onSelectRodeo: (rodeoId: string) => void;
}

function MonthGrid({
  month,
  today,
  rodeosByDay,
  registeredRodeoIds,
  itemNoun,
  onSelectRodeo,
}: MonthGridProps) {
  const { days, leadingPad } = useMemo(() => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    return {
      days: eachDayOfInterval({ start: monthStart, end: monthEnd }),
      // 0=Sun..6=Sat — number of empty cells before the 1st so it lands in
      // the correct weekday column.
      leadingPad: monthStart.getDay(),
    };
  }, [month]);

  return (
    <div className="w-fit">
      <div className="mb-2 flex items-baseline justify-center gap-1.5">
        <h3 className="text-sm font-semibold tracking-tight">
          {format(month, 'MMMM')}
        </h3>
        <span className="text-xs font-medium text-muted-foreground">
          {format(month, 'yyyy')}
        </span>
      </div>
      <div className="mb-1 grid grid-cols-7 border-b border-border/60 pb-1 text-center text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/80">
        {WEEKDAYS.map((wd) => (
          <div key={wd} className="h-5 w-8 leading-5">
            {wd.slice(0, 1)}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {Array.from({ length: leadingPad }).map((_, i) => (
          <div key={`pad-${i}`} aria-hidden="true" className="h-9 w-9" />
        ))}
        {days.map((d) => {
          const dayRodeos = rodeosByDay.get(dayKey(d)) ?? [];
          const isRegistered =
            !!registeredRodeoIds &&
            dayRodeos.some((r) => registeredRodeoIds.has(r.id));
          return (
            <DayCell
              key={d.getTime()}
              date={d}
              isToday={isSameDay(d, today)}
              rodeos={dayRodeos}
              isRegistered={isRegistered}
              itemNoun={itemNoun}
              onSelectRodeo={onSelectRodeo}
            />
          );
        })}
      </div>
    </div>
  );
}

interface DayCellProps {
  date: Date;
  isToday: boolean;
  rodeos: CalendarRodeo[];
  isRegistered: boolean;
  itemNoun: string;
  onSelectRodeo: (rodeoId: string) => void;
}

function DayCell({
  date,
  isToday,
  rodeos,
  isRegistered,
  itemNoun,
  onSelectRodeo,
}: DayCellProps) {
  const hasRodeos = rodeos.length > 0;
  const ariaDate = format(date, 'EEEE, MMMM d, yyyy');

  const baseClass = cn(
    'relative flex h-8 w-8 flex-col items-center justify-center rounded-md text-xs font-medium leading-none text-foreground/80 transition-all',
    isToday &&
      'font-bold text-primary ring-2 ring-primary ring-offset-1 ring-offset-background',
  );

  if (!hasRodeos) {
    return (
      <div className="flex items-center justify-center p-0.5">
        <div
          className={cn(baseClass, !isToday && 'hover:bg-muted/60')}
          aria-label={ariaDate}
        >
          <span>{date.getDate()}</span>
        </div>
      </div>
    );
  }

  const ariaSuffix = isRegistered
    ? ', you are entered'
    : '';

  return (
    <div className="flex items-center justify-center p-0.5">
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            baseClass,
            'cursor-pointer bg-gradient-to-b from-primary/20 to-primary/10 text-foreground shadow-sm ring-1 ring-inset ring-primary/30 hover:from-primary/30 hover:to-primary/20 hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            isToday &&
              'from-primary/30 to-primary/20 text-primary shadow ring-2 ring-primary',
          )}
          aria-label={`${ariaDate}, ${rodeos.length} ${itemNoun}${rodeos.length === 1 ? '' : 's'}${ariaSuffix}`}
        >
          <span>{date.getDate()}</span>
          {isRegistered && (
            <span
              aria-hidden="true"
              className="absolute bottom-1 block h-1 w-1 rounded-full bg-primary shadow-[0_0_4px_currentColor]"
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-72 p-0">
        <div className="border-b px-4 py-2">
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {format(date, 'EEEE')}
          </p>
          <p className="text-sm font-semibold">
            {format(date, 'MMMM d, yyyy')}
          </p>
        </div>
        <ul className="max-h-72 divide-y overflow-y-auto">
          {rodeos.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onSelectRodeo(r.id)}
                className="group flex w-full items-start gap-2 px-4 py-2.5 text-left transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{r.name}</p>
                  {r.organization_name && (
                    <p className="truncate text-xs font-medium text-muted-foreground">
                      {r.organization_name}
                    </p>
                  )}
                  <p className="truncate text-xs text-muted-foreground">
                    {formatDateRange(r.start_date, r.end_date)}
                  </p>
                  {r.location && (
                    <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground">
                      <MapPin
                        className="h-3 w-3 flex-shrink-0"
                        aria-hidden="true"
                      />
                      <span className="truncate">{r.location}</span>
                    </p>
                  )}
                  {r.userRegisteredEvents &&
                    r.userRegisteredEvents.length > 0 && (
                      <div className="mt-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                          Your events
                        </p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {r.userRegisteredEvents.map((ev) => (
                            <span
                              key={ev.eventId}
                              className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary"
                            >
                              {ev.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
                <ChevronRight
                  className="mt-1 h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
    </div>
  );
}
